import ScreenKey from '@/Enums/Screens/ScreenKey.enum';
import { IAgencyFormData, IAgencyPayload } from '@/Interfaces/Agency.interface';
import ModalHeaderIcon from '@/components/Icons/ModalHeaderIcon';
import LoadingBackdropWrapper from '@/components/Modal/common/LoadingBackdropWrapper';
import ModalTitleCard from '@/components/Modal/common/ModalTitleCard';
import { DEFAULT_AGENCY } from '@/constants/agencies';
import { createAgency, updateAgency } from '@/store/api/agencies.api';
import { useAppSelector } from '@/store/hooks';
import { selectMeScreenAccess } from '@/store/selectors/me.selectors';
import { Button, Dialog, DialogContent } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { toast } from 'react-toastify';
import AgencyInfo from './AgencyInfo';

interface IProps {
  form: UseFormReturn<IAgencyFormData>;
  open: boolean;
  onClose: (refresh?: boolean) => void;
  selectedAgency: IAgencyFormData | null;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  openAdvertiserModal: () => void;
}

const AgencyModal = ({ form, open, onClose, selectedAgency, loading, setLoading, openAdvertiserModal }: IProps) => {
  const { handleSubmit, reset } = form;

  const readOnly = useAppSelector(selectMeScreenAccess(ScreenKey.AGENCIES));

  useEffect(() => {
    if (selectedAgency) {
      reset(selectedAgency);
    } else {
      reset(DEFAULT_AGENCY);
    }
  }, [selectedAgency, reset]);

  const updateAgencyData = useCallback(
    async (id: number, payload: IAgencyPayload) => {
      setLoading(true);
      try {
        await updateAgency(id, payload);
        toast.success('Agency updated successfully');
        reset();
      } catch (error) {
        toast.error((error as Error).message || 'Something went wrong');
      } finally {
        setLoading(false);
      }
    },
    [setLoading, reset, updateAgency, toast],
  );

  const createNewAgency = useCallback(
    async (payload: IAgencyPayload) => {
      setLoading(true);
      try {
        await createAgency(payload);
        toast.success('Agency created successfully');
        reset();
      } catch (error) {
        toast.error((error as Error).message || 'Something went wrong');
      } finally {
        setLoading(false);
      }
    },
    [setLoading, reset],
  );

  const onSubmit = useCallback(
    async ({ id, name, client, advertisers }: IAgencyFormData) => {
      const payload: IAgencyPayload = {
        name,
        clientId: client!.value,
        advertiserIds: advertisers.map(advertiser => advertiser.value),
      };
      if (id && selectedAgency) {
        await updateAgencyData(id, payload);
      } else {
        await createNewAgency(payload);
      }
      onClose(true);
    },
    [createNewAgency, updateAgencyData, selectedAgency, onClose],
  );

  const handleClose = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);

  return (
    <LoadingBackdropWrapper backdropOpen={loading}>
      <Dialog maxWidth={false} open={open} onClose={handleClose} aria-labelledby='modal-title'>
        <DialogContent sx={{ width: '650px', overflow: 'hidden' }}>
          <ModalTitleCard
            titleIcon={<ModalHeaderIcon />}
            handleClose={handleClose}
            title={selectedAgency ? 'View Agency Details' : 'Create New Agency'}
          />
          <FormProvider {...form}>
            <form className='mt-4' onSubmit={handleSubmit(onSubmit)}>
              <AgencyInfo openAdvertiserModal={openAdvertiserModal} disabled={readOnly} />
              <hr className='-mx-[32px] my-5' />
              <div className='mt-5 flex justify-end gap-[12px]'>
                <Button color='inherit' variant='outlined' onClick={handleClose}>
                  Cancel
                </Button>
                <Button color='primary' variant='contained' type='submit' disabled={readOnly}>
                  {selectedAgency ? 'Save' : 'Create'}
                </Button>
              </div>
            </form>
          </FormProvider>
        </DialogContent>
      </Dialog>
    </LoadingBackdropWrapper>
  );
};

export default AgencyModal;
